import React, { useMemo, useState } from 'react';
import { Check, Info } from 'lucide-react';
import { formatRalDisplay, isValidHex, normaliseHex } from '../../colour/productColour';
import { listFinishesForComponent } from '../../colour/finishCompatibility';
import {
  CUSTOM_PRODUCT_COLOUR_ID,
  DEFAULT_PRODUCT_COLOUR_ID,
  DEFAULT_THRESHOLD_COLOUR_ID,
  getProductColourForDisplay,
  isCustomColourValue,
} from '../../colour/productColours';
import type { DoorViewSide } from '../../surfaces/doorViewSide';
import type { DoorConfiguration } from '../../types/doorConfiguration';
import {
  applyColourForViewSide,
  colourIdForViewSide,
  type ColourSurface,
} from '../../colour/viewSideColours';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../../ui/drawer';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../ui/cn';
import { COLOUR_CATEGORIES, coloursInCategory } from '../colourCategories';
import { useIsDesktop } from '../useMediaQuery';
import { ViewSideToggle } from '../ViewSideToggle';

const SURFACE_SECTIONS: Array<{ id: ColourSurface; heading: string; ariaName: string }> = [
  { id: 'slab', heading: 'Door colour', ariaName: 'Door colour' },
  { id: 'frame', heading: 'Frame colour', ariaName: 'Frame colour' },
  { id: 'cill', heading: 'Cill colour', ariaName: 'Cill colour' },
  { id: 'threshold', heading: 'Threshold colour', ariaName: 'Threshold colour' },
];

function defaultColourIdForSurface(surface: ColourSurface): string {
  return surface === 'threshold' ? DEFAULT_THRESHOLD_COLOUR_ID : DEFAULT_PRODUCT_COLOUR_ID;
}

const CustomFinishFields: React.FC<{
  hexInput: string;
  ralInput: string;
  onHexInput: (value: string) => void;
  onRalInput: (value: string) => void;
  onApply: () => void;
}> = ({ hexInput, ralInput, onHexInput, onRalInput, onApply }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-2">
      <Label htmlFor="custom-ral">RAL (optional)</Label>
      <Input
        id="custom-ral"
        aria-label="Custom RAL"
        placeholder="RAL (optional)"
        value={ralInput}
        onChange={(event) => onRalInput(event.target.value)}
      />
    </div>
    <div className="flex flex-col gap-2">
      <Label htmlFor="custom-hex">HEX</Label>
      <Input
        id="custom-hex"
        aria-label="Custom HEX"
        placeholder="#RRGGBB"
        value={hexInput}
        onChange={(event) => onHexInput(event.target.value)}
      />
    </div>
    <Button type="button" onClick={onApply}>
      Apply custom colour
    </Button>
  </div>
);

export const ColourStage: React.FC<{
  configuration: DoorConfiguration;
  viewSide: DoorViewSide;
  onViewSideChange: (side: DoorViewSide) => void;
  onChange: (next: DoorConfiguration) => void;
}> = ({ configuration, viewSide, onViewSideChange, onChange }) => {
  const isDesktop = useIsDesktop();
  const surfaces = useMemo(
    () =>
      SURFACE_SECTIONS.filter((section) => {
        if (section.id === 'cill') return configuration.ancillaries.hasCill;
        if (section.id === 'threshold') return configuration.ancillaries.hasThreshold;
        return true;
      }),
    [configuration.ancillaries.hasCill, configuration.ancillaries.hasThreshold]
  );
  const [categoryId, setCategoryId] = useState(COLOUR_CATEGORIES[0].id);
  const [customSurface, setCustomSurface] = useState<ColourSurface>('slab');
  const [customOpen, setCustomOpen] = useState(false);
  const [customHex, setCustomHex] = useState('#F1F0EA');
  const [customRal, setCustomRal] = useState('');

  const category = COLOUR_CATEGORIES.find((item) => item.id === categoryId) ?? COLOUR_CATEGORIES[0];

  const applyCustom = () => {
    if (!isValidHex(customHex)) return;
    const hex = normaliseHex(customHex);
    if (!hex) return;
    onChange(applyColourForViewSide(configuration, customSurface, viewSide, hex));
    setCustomOpen(false);
  };

  const customForm = (
    <CustomFinishFields
      hexInput={customHex}
      ralInput={customRal}
      onHexInput={setCustomHex}
      onRalInput={setCustomRal}
      onApply={applyCustom}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <ViewSideToggle
        viewSide={viewSide}
        onChange={onViewSideChange}
        ariaLabel="Door face"
      />

      <div className="flex flex-wrap gap-2">
        {COLOUR_CATEGORIES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setCategoryId(entry.id)}
            className={cn(
              'door-designer-chip rounded-full border border-solid px-3 py-1.5 text-sm',
              categoryId === entry.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-foreground hover:bg-muted'
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {surfaces.map((section) => {
        const value = colourIdForViewSide(configuration, section.id, viewSide);
        const selected = getProductColourForDisplay(value);
        const swatches = coloursInCategory(category, listFinishesForComponent(section.id));
        const facePrefix = viewSide === 'internal' ? 'Internal ' : '';
        const defaultColourId = defaultColourIdForSurface(section.id);
        const defaultInCategory = swatches.some((colour) => colour.id === defaultColourId);
        const isDefaultSelected = value === defaultColourId && !defaultInCategory;

        return (
          <section key={section.id}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {section.heading}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {swatches.map((colour) => {
                const isCustom = colour.id === CUSTOM_PRODUCT_COLOUR_ID;
                const isSelected = isCustom ? isCustomColourValue(value) : value === colour.id;
                const swatchHex = isCustom && isSelected ? selected.hex : colour.hex;
                return (
                  <button
                    key={colour.id}
                    type="button"
                    aria-label={`${facePrefix}${section.ariaName} ${colour.name}`}
                    aria-pressed={isSelected}
                    title={
                      isCustom
                        ? `${colour.name} · Enter HEX`
                        : `${colour.name} · ${colour.ral ?? colour.hex}`
                    }
                    onClick={() => {
                      if (isCustom) {
                        setCustomSurface(section.id);
                        setCustomHex(isCustomColourValue(value) ? selected.hex : colour.hex);
                        setCustomOpen(true);
                        return;
                      }
                      onChange(applyColourForViewSide(configuration, section.id, viewSide, colour.id));
                    }}
                    className="door-designer-swatch flex min-w-0 cursor-pointer flex-col items-stretch gap-1.5 border-0 bg-transparent p-0 shadow-none"
                  >
                    <span
                      className={cn(
                        'relative block aspect-[4/3] w-full overflow-hidden rounded-sm',
                        isSelected
                          ? 'ring-2 ring-[#c8c8c4] ring-offset-2 ring-offset-card'
                          : 'ring-1 ring-border'
                      )}
                      style={{ backgroundColor: swatchHex }}
                    >
                      {isSelected ? (
                        <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xs">
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </span>
                      ) : null}
                    </span>
                    <span className="line-clamp-2 text-center text-[11px] font-medium leading-tight text-foreground">
                      {colour.name}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                aria-label={`${facePrefix}${section.ariaName} Default`}
                aria-pressed={isDefaultSelected}
                title={
                  section.id === 'threshold' ? 'Default · Aluminium' : 'Default · White'
                }
                onClick={() =>
                  onChange(
                    applyColourForViewSide(
                      configuration,
                      section.id,
                      viewSide,
                      defaultColourId
                    )
                  )
                }
                className="door-designer-swatch flex min-w-0 cursor-pointer flex-col items-stretch gap-1.5 border-0 bg-transparent p-0 shadow-none"
              >
                <span
                  className={cn(
                    'door-designer-swatch-default relative block aspect-[4/3] w-full overflow-hidden rounded-sm',
                    isDefaultSelected
                      ? 'ring-2 ring-[#c8c8c4] ring-offset-2 ring-offset-card'
                      : 'ring-1 ring-border'
                  )}
                >
                  <span className="door-designer-swatch-default-slash" aria-hidden />
                  {isDefaultSelected ? (
                    <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xs">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  ) : null}
                </span>
                <span className="line-clamp-2 text-center text-[11px] font-medium leading-tight text-foreground">
                  Default
                </span>
              </button>
            </div>
            {isCustomColourValue(value) ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Custom {selected.hex}
                {` · ${formatRalDisplay(selected, customRal)}`}
              </p>
            ) : null}
          </section>
        );
      })}

      <p className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        External and internal colours are independent. Changing one face does not change the other.
      </p>

      {isDesktop ? (
        <Dialog open={customOpen} onOpenChange={setCustomOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Custom RAL</DialogTitle>
              <DialogDescription>
                Enter a HEX colour. RAL text is optional display metadata.
              </DialogDescription>
            </DialogHeader>
            {customForm}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={customOpen} onOpenChange={setCustomOpen}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Custom RAL</DrawerTitle>
              <DrawerDescription>
                Enter a HEX colour. RAL text is optional display metadata.
              </DrawerDescription>
            </DrawerHeader>
            <div className="p-4">{customForm}</div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
};
